import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
// Base toastr styling. Kept global (before the overrides so those win) because
// 13 modules import `toastr` directly and render toasts without loading the
// stylesheet themselves — the lazy ../utils/toast wrapper only covers its own
// callers. Without this, the first toast on those pages renders unpositioned.
import "toastr/build/toastr.min.css";
import "./styles/toastr-overrides.css";


import "./index.css";
// Base styling for react-phone-number-input. Kept global because Checkout,
// Payment, PaymentStatus, IdleBehaviour and MembershipDealsModal all render
// <PhoneInput> without importing the stylesheet themselves.
import "react-phone-number-input/style.css";

import { store } from "./redux/store";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { deferSentryInitialization } from "./utils/sentry";
import "./i18n";
import { SentryErrorBoundary } from "./ErrorBoundry";
import lazyWithRetry from "./utils/lazyWithRetry";

const homeChunkPromise = import("./Home");
const Home = lazyWithRetry(() => homeChunkPromise);

if (typeof window !== "undefined" && window.location) {
  const cleanPathname = window.location.pathname.replace(
    /^\/(cm-preview\/whatsapp|whatsapp)(?=\/|$)/,
    ""
  );
  const firstSeg = (cleanPathname.split("/").filter(Boolean)[0] || "").toLowerCase();
  const nonMainPageRoutes = {
    "about-us": true,
    "ai-sales-concierge": true,
    "blogs": true,
    "booking-engine": true,
    "bookings": true,
    "cancellation": true,
    "checkout": true,
    "cm-preview": true,
    "corporate-booking": true,
    "getyourreport": true,
    "ihg-guide": true,
    "marketing": true,
    "modification": true,
    "morerevenue": true,
    "move": true,
    "offers": true,
    "pagenotfound": true,
    "payment": true,
    "payment-status": true,
    "planner": true,
    "privacy-policy": true,
    "redirect": true,
    "refund-policy": true,
    "review": true,
    "roi": true,
    "room": true,
    "room-view": true,
    "select-rooms": true,
    "sitemaps": true,
    "terms-conditions": true,
  };
  if (!nonMainPageRoutes[firstSeg]) {
    window.__HZ_MAINPAGE_CHUNK__ = import("./pages/MainPage/MainPage");
  }
}

// Guard against NotFoundError in Node.prototype.removeChild / insertBefore
// caused by Chrome/Safari Translate, Razorpay, Bot9, or GTM mutating DOM nodes
// managed by React (which triggered page_crashed on /room/checkout today).
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.apply(this, [newNode, null]);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));

// googleAnalytics stays out of the entry chunk: web-vitals callbacks fire
// after load, so loading the module on first callback costs nothing.
// Events land on window.dataLayer either way — nothing is dropped.
const trackPerformance = (obj) => {
  import("./utils/googleAnalytics").then(({ fireGtmEvent }) =>
    fireGtmEvent(obj?.name, { perfomanceObj: obj }),
  );
};

function MainApp() {
  return (
    // <CacheBuster
    //
    //   currentVersion={packageJson.version}
    //   isEnabled={true} //If false, the library is disabled.
    //   isVerboseMode={true} //If true, the library writes verbose logs to console.
    //   loadingComponent={<Loader />} //If not pass, nothing appears at the time of new version check.
    //   metaFileDirectory={"."} //If public assets are hosted somewhere other than root on your server.
    // >
    <SentryErrorBoundary>
      <Provider store={store}>
        <Router>
          <Home />
        </Router>
      </Provider>
    </SentryErrorBoundary>
    // </CacheBuster>
  );
}
// const CacheClearedComponent = withClearCache(MainApp);
root.render(<MainApp />);
deferSentryInitialization();

// Enable service worker for instant repeat visits
if (process.env.NODE_ENV === "production") {
  serviceWorkerRegistration.register({
    onUpdate: () => {
      // Do not force mid-session page reloads when background SW installation finishes.
    },
  });
} else {
  serviceWorkerRegistration.unregister();
}

reportWebVitals(trackPerformance);
window.__HZ_BUILD__ = "20261023";

